// Blog Page
import React from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver.jsx';
import Footer from '../components/Footer.jsx';

function BlogPage() {
  const [headerRef, headerVisible] = useIntersectionObserver({ threshold: 0.2 });

  // Blog posts
  const blogPosts = [
    {
      id: 1,
      slug: 'when-rest-feels-unsafe',
      title: 'When Rest Feels Unsafe',
      excerpt: 'You\'ve tried to rest—the retreats, therapy, journaling—and still, you can\'t fully let go. Because the exhaustion isn\'t just from your schedule, it\'s from years of conditioning.',
      readTime: '5 min read',
      category: 'Nervous System Healing'
    },
    {
      id: 2,
      slug: 'deeper-than-boundaries',
      title: 'This Work Goes Deeper Than Just "Setting Better Boundaries"',
      excerpt: 'People-pleasing isn\'t a character flaw—it\'s a nervous system response called fawning. Learn how to regulate your nervous system and build a felt sense of safety in your body.',
      readTime: '7 min read',
      category: 'Nervous System Healing'
    },
    {
      id: 3,
      slug: 'nervous-system-safety',
      title: 'Your Nervous System Is Either Creating Safety... Or Keeping You Stuck',
      excerpt: 'Most people-pleasers are exceptional at reading the room. But this hyper-awareness comes at a cost. Learn why you don\'t need more boundary scripts—you need nervous system regulation.',
      readTime: '8 min read',
      category: 'Nervous System Healing'
    },
    {
      id: 4,
      slug: 'breaking-the-cycle',
      title: 'Breaking the Cycle: End Generational Trauma in Your Lifetime',
      excerpt: 'The pain of people-pleasing wasn\'t your fault—it was passed down through generations. But here\'s the profound truth: The cycle can end with you.',
      readTime: '6 min read',
      category: 'Generational Healing'
    },
    {
      id: 5,
      slug: 'people-pleasing-coping-mechanism',
      title: 'People-Pleasing Is a Brilliant Coping Mechanism',
      excerpt: 'When emotional attunement was missing, you adapted by becoming hyper-attuned to others. You learned to read the room, anticipate needs, adjust your behavior...',
      readTime: '7 min read',
      category: 'Understanding People-Pleasing'
    },
    {
      id: 6,
      slug: 'emotional-neglect',
      title: 'The Invisible Wound: Understanding Emotional Neglect',
      excerpt: 'Emotional neglect is one of the most invisible forms of trauma because it\'s defined by what didn\'t happen rather than what did...',
      readTime: '8 min read',
      category: 'Healing'
    },
    {
      id: 7,
      slug: 'coaching-vs-therapy',
      title: 'Do I Need Coaching or Therapy?',
      excerpt: 'Many clients experience profound transformation through coaching alone — it can be incredibly powerful work that helps you shift long-standing patterns...',
      readTime: '6 min read',
      category: 'Support'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50 pt-20">
      <section className="py-20 md:py-32 relative overflow-hidden">
        {/* Ambient Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sage-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-warm-200/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header with Animation */}
          <div 
            ref={headerRef}
            className={`text-center mb-16 transition-all duration-800 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-sage-800 mb-6 text-balance">
              blog
            </h1>
          </div>

          {/* Blog Posts Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {blogPosts.map((post) => (
              <Link 
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group glass-light border-2 border-white/30 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-[1.02]"
              >
                {/* Post Image Placeholder */}
                <div className="h-48 bg-gradient-to-br from-sage-200 to-warm-200 relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
                
                {/* Post Content */}
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-sage-100 text-sage-700 text-xs font-semibold rounded-full mb-3">
                    {post.category}
                  </span>
                  <h2 className="text-xl md:text-2xl font-serif font-medium text-sage-800 mb-3 group-hover:text-warm-600 transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-sm text-gray-500">
                    <span>{post.readTime}</span>
                    <span className="mx-2">•</span>
                    <span className="text-warm-600 group-hover:underline">Read more →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center p-8 glass-light border border-white/30 rounded-3xl">
            <h3 className="text-2xl md:text-3xl font-serif font-light text-sage-700 mb-4">
              Want More Resources?
            </h3>
            <p className="text-sage-600 mb-6 max-w-2xl mx-auto">
              Get my free guide on reclaiming your authentic self and breaking free from people-pleasing patterns.
            </p>
            <Link
              to="/lead-magnet"
              className="inline-block px-8 py-4 bg-warm-500 text-white rounded-full text-base font-semibold hover:bg-warm-600 transition-all duration-300 hover:shadow-xl transform hover:scale-105"
            >
              Get My Free Guide
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default BlogPage;
